import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clearCachedAssets } from '../api/assets';
import { useAuth } from '../auth/AuthProvider';
import { isUsageBlocked, subscribeUsageGate } from '../auth/legalGateSignal';
import { clearExplicitLogoutPending, isExplicitLogoutPending } from '../auth/logoutSignal';
import { isCapacitorNativeSync } from '../native/bridge';
import {
  broadcastLocalDatabaseClose,
  closeAllLocalDatabases,
  closeAndDeleteAllLocalDatabases,
  closeForUser,
  openForUser,
  subscribeLocalDatabaseClose,
  type LorathLocalDatabase,
} from './db';
import { SyncEngine, type BootstrapProgress, type SyncEngineSnapshot, type SyncStatus } from './engine';

export interface SyncContextValue {
  status: SyncStatus;
  lastSyncAt: string | null;
  // Warum: nur befuellt, waehrend status === 'bootstrapping'. Sonst null,
  // damit die UI nicht aus Versehen einen stehengebliebenen Balken anzeigt.
  bootstrapProgress: BootstrapProgress | null;
  db: LorathLocalDatabase | null;
  forceSync: () => Promise<void>;
}

const idleSnapshot: SyncEngineSnapshot = { status: 'idle', lastSyncAt: null };
const LAST_LOCAL_USER_ID_KEY = 'lorath_sync_last_user_id_v1';

const SyncContext = createContext<SyncContextValue | null>(null);

function readLastLocalUserId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(LAST_LOCAL_USER_ID_KEY);
}

function writeLastLocalUserId(userId: number | string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LAST_LOCAL_USER_ID_KEY, String(userId));
}

function clearLastLocalUserId(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LAST_LOCAL_USER_ID_KEY);
}

// Warum: „best-effort"-Storage darf der Browser jederzeit raeumen — Safari-ITP
// wirft die IndexedDB nach ~7 Tagen Inaktivitaet weg, danach liefe im Web der
// komplette Erststart-Download erneut. Der Antrag ist best-effort und stumm:
// lehnt der Browser ab (oder kennt die API nicht), aendert sich schlicht nichts.
// Nativ ist der Speicher ohnehin persistent, dort sparen wir uns den Aufruf.
let persistentStorageRequested = false;

function requestPersistentStorage(): void {
  if (persistentStorageRequested || isCapacitorNativeSync()) return;
  persistentStorageRequested = true;
  if (typeof navigator === 'undefined') return;
  const storage = (navigator as Partial<Navigator>).storage;
  if (typeof storage?.persist !== 'function' || typeof storage.persisted !== 'function') return;

  void (async () => {
    try {
      if (await storage.persisted()) return;
      await storage.persist();
    } catch {
      /* ignore — kein UI-Effekt, eine Ablehnung ist kein Fehlerfall */
    }
  })();
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [engine, setEngine] = useState<SyncEngine | null>(null);
  const [db, setDb] = useState<LorathLocalDatabase | null>(null);
  const [snapshot, setSnapshot] = useState<SyncEngineSnapshot>(idleSnapshot);
  const engineRef = useRef<SyncEngine | null>(null);
  const lastUserIdRef = useRef<string | null>(null);
  const userId = auth.user?.userId ?? null;

  useEffect(() => {
    let cancelled = false;
    let nextEngine: SyncEngine | null = null;

    const stopCurrent = () => {
      engineRef.current?.stop();
      engineRef.current = null;
      setEngine(null);
      setDb(null);
      setSnapshot(idleSnapshot);
    };

    const reconcile = async () => {
      const explicitLogoutPending = auth.logoutRequested || isExplicitLogoutPending();
      if (auth.status === 'loading' && !explicitLogoutPending) return;

      const persistedUserId = readLastLocalUserId();
      let previousUserId = lastUserIdRef.current ?? persistedUserId;

      if (explicitLogoutPending) {
        stopCurrent();
        lastUserIdRef.current = null;
        try {
          // Nur die explizite Logout-Aktion ist eine Privacy-Grenze. Ein
          // transienter Guest-Boot (z.B. Storage-/OTA-Problem) darf die lokale
          // Offline-Datenbank und Asset-Blobs niemals vernichten.
          //
          // W5-E: Geloescht werden ausschliesslich die NUTZER-Datenbanken
          // (`lorath-local-u<id>`). Die kontouebergreifende Katalog-Datenbank
          // (`lorath-catalog-v1`, catalogDb.ts) bleibt bewusst stehen — sie
          // enthaelt nur geteilte Vorlagen ohne jeden Personenbezug, und ihr
          // Erhalt ist der ganze Zweck des Umbaus (Plan 5.4). Die persoenliche
          // Sicht darauf (Verweisliste `catalogVisible`) liegt IN der
          // Nutzer-DB und faellt mit ihr.
          clearCachedAssets();
          broadcastLocalDatabaseClose();
          await closeAndDeleteAllLocalDatabases(previousUserId ? [previousUserId] : []);
          clearLastLocalUserId();
          // Erst NACH erfolgreichem DB-Wipe konsumieren. Ein Reload oder Fehler
          // laesst den Marker stehen und der naechste Boot versucht es erneut.
          clearExplicitLogoutPending();
        } catch (error) {
          console.warn('[Sync] Local DB privacy cleanup failed after logout.', error);
          return;
        }

        // Reload-Randfall: Der persistierte Marker kann einen unterbrochenen
        // Logout ueberleben, waehrend /me wieder erfolgreich authed ist. Nach
        // dem nachgeholten Wipe im selben Reconcile frisch fuer diesen User
        // starten; beim aktuell laufenden Logout dagegen auf den Reload warten.
        if (auth.logoutRequested || auth.status !== 'authed' || userId === null) return;
        previousUserId = null;
      }

      if (auth.status !== 'authed' || userId === null) {
        stopCurrent();
        return;
      }

      const nextUserId = String(userId);
      if (previousUserId !== null && previousUserId !== nextUserId) {
        stopCurrent();
        // User-Wechsel ist ebenfalls eine Privacy-Grenze: alte Asset-Object-URLs weg.
        // Der geteilte Vorlagenkatalog bleibt auch hier erhalten (s. o.) — das
        // zweite Konto auf demselben Geraet laedt ihn nicht erneut herunter.
        clearCachedAssets();
        broadcastLocalDatabaseClose();
        try {
          await closeAndDeleteAllLocalDatabases([previousUserId, nextUserId]);
        } catch (error) {
          console.warn('[Sync] Local DB privacy cleanup failed during user switch.', error);
        }
      }

      if (cancelled) return;

      lastUserIdRef.current = nextUserId;
      writeLastLocalUserId(nextUserId);
      const nextDb = openForUser(nextUserId);
      nextEngine = new SyncEngine({ db: nextDb, queryClient });

      if (cancelled) {
        nextEngine.stop();
        closeForUser(nextUserId);
        return;
      }

      engineRef.current = nextEngine;
      setDb(nextDb);
      setEngine(nextEngine);
      setSnapshot(nextEngine.getSnapshot());
      // Offenes Startup-Gate (Rechtstexte) → Bootstrap wartet, bis es 'clear'
      // ist (legalGateSignal.ts). VOR start(), damit kein Anlauf durchrutscht.
      nextEngine.setUsageBlocked(isUsageBlocked(nextUserId));
      nextEngine.start();
      requestPersistentStorage();
    };

    void reconcile().catch((error) => {
      console.warn('[Sync] Local sync reconciliation failed.', error);
    });

    return () => {
      cancelled = true;
      if (nextEngine) {
        nextEngine.stop();
        closeForUser(userId ?? '');
        if (engineRef.current === nextEngine) engineRef.current = null;
      }
    };
  }, [auth.logoutRequested, auth.status, queryClient, userId]);

  useEffect(() => subscribeLocalDatabaseClose(() => {
    engineRef.current?.stop();
    engineRef.current = null;
    // Cross-Tab-Close (anderer Tab hat ausgeloggt): auch hier Asset-URLs freigeben.
    clearCachedAssets();
    closeAllLocalDatabases();
    setEngine(null);
    setDb(null);
    setSnapshot(idleSnapshot);
  }), []);

  // Gate-Wechsel (Zustimmung erteilt, neue Version gemeldet) an die laufende
  // Engine weiterreichen; Entsperren startet den Bootstrap sofort.
  useEffect(() => {
    if (!engine) return undefined;
    const apply = () => engine.setUsageBlocked(isUsageBlocked(userId));
    apply();
    return subscribeUsageGate(apply);
  }, [engine, userId]);

  useEffect(() => {
    if (!engine) return undefined;
    setSnapshot(engine.getSnapshot());
    return engine.subscribe(() => {
      setSnapshot(engine.getSnapshot());
    });
  }, [engine]);

  const forceSync = useCallback(async () => {
    await engine?.forceSync();
  }, [engine]);

  const value = useMemo<SyncContextValue>(() => ({
    status: snapshot.status,
    lastSyncAt: snapshot.lastSyncAt,
    bootstrapProgress: snapshot.bootstrapProgress ?? null,
    db,
    forceSync,
  }), [db, forceSync, snapshot.bootstrapProgress, snapshot.lastSyncAt, snapshot.status]);

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync(): SyncContextValue {
  const value = useContext(SyncContext);
  if (!value) throw new Error('useSync must be used within SyncProvider');
  return value;
}

export function useSyncDatabase(): LorathLocalDatabase | null {
  // Toleriert das Fehlen eines SyncProvider (z.B. in Unit-Tests, die eine
  // Seite isoliert rendern) — der Rückgabetyp ist ohnehin nullable und alle
  // Aufrufer nutzen `db?.`. So wirft der optionale Local-First-Zugriff nicht.
  const value = useContext(SyncContext);
  return value?.db ?? null;
}
